export const MOCK_WORKSPACE = {
    name: "Support",
    title: "Kore Workspace",
};

export const MOCK_USER = {
    name: "Cole Lockwood",
    initials: "CL",
    email: "cole@claritick.com",
};

export const MOCK_DEPARTMENTS = [
    { id: 1, name: "Customer Support", color: "#92400e", users: 3, emails: 2, tickets: 53 },
    { id: 2, name: "Development", color: "#0087e2", users: 5, emails: 4, tickets: 178 },
    { id: 3, name: "Social Media", color: "#db2777", users: 2, emails: 1, tickets: 12 },
    { id: 4, name: "Billing", color: "#059669", users: 2, emails: 2, tickets: 34 },
    { id: 5, name: "Sales", color: "#7c3aed", users: 4, emails: 1, tickets: 28 },
    { id: 6, name: "Onboarding", color: "#d97706", users: 2, emails: 1, tickets: 19 },
];

export const MOCK_UNASSIGNED_DEPARTMENT = {
    name: "Unassigned",
    color: "#9ca3af",
};

export function buildDepartmentOptions(departments = []) {
    return [
        {
            value: MOCK_UNASSIGNED_DEPARTMENT.name,
            label: MOCK_UNASSIGNED_DEPARTMENT.name,
            color: MOCK_UNASSIGNED_DEPARTMENT.color,
        },
        ...departments.map((d) => ({
            value: d.name,
            label: d.name,
            color: d.color,
        })),
    ];
}

export const MOCK_USERS = [
    { id: 1, name: "John Doe", title: "Software Dev", email: "john@example.com", role: "Admin", accepted: true, joined: "12 May 2026" },
    { id: 2, name: "Jane Smith", title: "Dev", email: "jane@example.com", role: "Agent", accepted: true, joined: "18 Mar 2026" },
    { id: 3, name: "Alex Morgan", title: "Support Lead", email: "alex@example.com", role: "Agent", accepted: true, joined: "3 Apr 2026" },
    { id: 4, name: "Riya Patel", title: "Customer Success", email: "riya@example.com", role: "Agent", accepted: true, joined: "2 Feb 2026" },
    { id: 5, name: "Marcus Webb", title: "Billing", email: "marcus@example.com", role: "Agent", accepted: true, joined: "10 Jun 2026" },
    { id: 6, name: "Laura Bennett", title: "Customer", email: "lbennett@gmail.com", role: "Customer", accepted: true, joined: "1 Jan 2026" },
];

export function matchesMockSearch(query, ...fields) {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return fields.some((field) => String(field ?? "").toLowerCase().includes(q));
}

const SEARCH_GROUP_LABELS = {
    ticket: "Tickets",
    user: "Users",
    email: "Incoming emails",
    department: "Departments",
};

export function searchMockApp(query, { tickets = [], users = [], emails = [], departments = [] } = {}) {
    const q = query.trim();
    if (!q) return [];

    const results = [];

    tickets
        .filter((t) => matchesMockSearch(q, t.title, t.ticketId, t.department, t.assignee, t.createdBy, t.preview))
        .slice(0, 4)
        .forEach((t) => {
            results.push({
                type: "ticket",
                id: t.id,
                label: t.title,
                meta: `#${t.ticketId} · ${t.department}`,
                view: "ticket-detail",
                ticketId: t.id,
            });
        });

    users
        .filter((u) => matchesMockSearch(q, u.name, u.email, u.title, u.role))
        .slice(0, 4)
        .forEach((u) => {
            results.push({
                type: "user",
                id: u.id,
                label: u.name,
                meta: [u.title, u.email].filter(Boolean).join(" · "),
                view: "users",
            });
        });

    emails
        .filter((e) => matchesMockSearch(q, e.name, e.address, e.department))
        .slice(0, 4)
        .forEach((e) => {
            results.push({
                type: "email",
                id: e.id,
                label: e.address,
                meta: [e.name, e.department].filter(Boolean).join(" · "),
                view: "emails",
            });
        });

    departments
        .filter((d) => matchesMockSearch(q, d.name))
        .slice(0, 4)
        .forEach((d) => {
            results.push({
                type: "department",
                id: d.id,
                label: d.name,
                meta: `${d.tickets} tickets · ${d.users} users`,
                view: "departments",
            });
        });

    return results;
}

export function groupMockSearchResults(results) {
    const order = ["ticket", "user", "email", "department"];
    return order
        .map((type) => ({
            type,
            label: SEARCH_GROUP_LABELS[type],
            items: results.filter((r) => r.type === type),
        }))
        .filter((group) => group.items.length > 0);
}

export function getMockUserInitials(name) {
    const parts = name.trim().split(/\s+/);
    if (parts.length === 0) return "";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function buildAssigneeOptions(users) {
    return [
        { value: "", label: "Unassigned", color: "#9ca3af" },
        ...users.filter((u) => u.accepted).map((u) => ({
            value: getMockUserInitials(u.name),
            label: u.name,
            color: "#0087e2",
        })),
    ];
}

export const MOCK_ASSIGNEE_OPTIONS = buildAssigneeOptions(MOCK_USERS);

export const MOCK_EMAILS = [
    { id: 1, name: "Kore Support", address: "support@kore.co", received: 8, department: "dev", deptColor: "#0087e2" },
    { id: 2, name: "Your Company Support", address: "yourcompany@claritick.com", received: 20, department: "Customer Support", deptColor: "#92400e" },
];

export const MOCK_EMBED_FORMS = [
    { id: 1, name: "Contact Form", department: "Customer Support", active: true, submissions: 42 },
    { id: 2, name: "Technical Support", department: "Development", active: true, submissions: 87 },
];

export const MOCK_STATS = {
    open: 23,
    active: 6,
    inProgress: 0,
    overdue: 0,
    pending: 3,
    total: 43,
    clarification: 0,
    confirmation: 0,
    closed: 10,
};

export const MOCK_DASHBOARD = {
    topAgents: [
        { name: "John Doe", resolved: 18 },
        { name: "Jane Smith", resolved: 3 },
    ],
    ticketsCreated: [2, 1, 4, 3, 5, 2, 1, 6, 8, 3, 4, 2, 5, 7, 9, 6, 4, 5, 2, 8, 4, 3, 6, 5, 4, 7, 28, 5, 15, 6],
    byStatus: [
        { label: "Open", value: 23, color: "#0087e2" },
        { label: "Closed", value: 10, color: "#12a364" },
        { label: "Pending", value: 3, color: "#f59e0b" },
        { label: "Active", value: 6, color: "#7c3aed" },
    ],
    byPriority: [
        { label: "Low", value: 12, color: "#94a3b8" },
        { label: "Medium", value: 15, color: "#c4a882" },
        { label: "High", value: 16, color: "#eab308" },
    ],
    byDepartment: [
        { label: "dev", value: 31, color: "#0087e2" },
        { label: "Customer Support", value: 10, color: "#92400e" },
        { label: "socials", value: 2, color: "#db2777" },
    ],
};

const MOCK_DASHBOARD_BY_USER = {
    [MOCK_USER.name]: {
        stats: MOCK_STATS,
        dashboard: MOCK_DASHBOARD,
    },
    "John Doe": {
        stats: {
            open: 8, active: 4, inProgress: 2, overdue: 1, pending: 0,
            total: 31, clarification: 0, confirmation: 1, closed: 15,
        },
        dashboard: {
            topAgents: [
                { name: "John Doe", resolved: 24 },
                { name: "Alex Morgan", resolved: 11 },
            ],
            ticketsCreated: [1, 2, 3, 4, 5, 6, 4, 3, 5, 7, 8, 6, 5, 4, 3, 6, 7, 8, 9, 7, 6, 5, 4, 8, 9, 10, 12, 8, 9, 7],
            byStatus: [
                { label: "Open", value: 8, color: "#0087e2" },
                { label: "Closed", value: 15, color: "#12a364" },
                { label: "Pending", value: 0, color: "#f59e0b" },
                { label: "Active", value: 4, color: "#7c3aed" },
            ],
            byPriority: [
                { label: "Low", value: 6, color: "#94a3b8" },
                { label: "Medium", value: 14, color: "#c4a882" },
                { label: "High", value: 11, color: "#eab308" },
            ],
            byDepartment: [
                { label: "dev", value: 22, color: "#0087e2" },
                { label: "Customer Support", value: 5, color: "#92400e" },
                { label: "Billing", value: 4, color: "#059669" },
            ],
        },
    },
    "Jane Smith": {
        stats: {
            open: 5, active: 2, inProgress: 1, overdue: 0, pending: 4,
            total: 18, clarification: 2, confirmation: 0, closed: 6,
        },
        dashboard: {
            topAgents: [
                { name: "Jane Smith", resolved: 9 },
                { name: "Riya Patel", resolved: 7 },
            ],
            ticketsCreated: [0, 1, 1, 2, 1, 0, 2, 1, 3, 2, 1, 1, 2, 3, 2, 1, 2, 1, 0, 2, 3, 2, 1, 2, 1, 3, 4, 2, 3, 2],
            byStatus: [
                { label: "Open", value: 5, color: "#0087e2" },
                { label: "Closed", value: 6, color: "#12a364" },
                { label: "Pending", value: 4, color: "#f59e0b" },
                { label: "Active", value: 2, color: "#7c3aed" },
            ],
            byPriority: [
                { label: "Low", value: 8, color: "#94a3b8" },
                { label: "Medium", value: 7, color: "#c4a882" },
                { label: "High", value: 3, color: "#eab308" },
            ],
            byDepartment: [
                { label: "dev", value: 12, color: "#0087e2" },
                { label: "Customer Support", value: 4, color: "#92400e" },
                { label: "Sales", value: 2, color: "#7c3aed" },
            ],
        },
    },
    "Alex Morgan": {
        stats: {
            open: 14, active: 5, inProgress: 3, overdue: 2, pending: 2,
            total: 36, clarification: 3, confirmation: 2, closed: 8,
        },
        dashboard: {
            topAgents: [
                { name: "Alex Morgan", resolved: 16 },
                { name: "Cole Lockwood", resolved: 12 },
            ],
            ticketsCreated: [3, 4, 5, 3, 6, 4, 5, 7, 6, 5, 4, 6, 7, 8, 6, 5, 7, 6, 5, 8, 7, 6, 5, 7, 8, 9, 11, 7, 8, 6],
            byStatus: [
                { label: "Open", value: 14, color: "#0087e2" },
                { label: "Closed", value: 8, color: "#12a364" },
                { label: "Pending", value: 2, color: "#f59e0b" },
                { label: "Active", value: 5, color: "#7c3aed" },
            ],
            byPriority: [
                { label: "Low", value: 10, color: "#94a3b8" },
                { label: "Medium", value: 12, color: "#c4a882" },
                { label: "High", value: 14, color: "#eab308" },
            ],
            byDepartment: [
                { label: "Customer Support", value: 18, color: "#92400e" },
                { label: "dev", value: 9, color: "#0087e2" },
                { label: "Onboarding", value: 5, color: "#d97706" },
            ],
        },
    },
    "Riya Patel": {
        stats: {
            open: 7, active: 3, inProgress: 0, overdue: 0, pending: 1,
            total: 22, clarification: 1, confirmation: 0, closed: 11,
        },
        dashboard: {
            topAgents: [
                { name: "Riya Patel", resolved: 14 },
                { name: "Jane Smith", resolved: 6 },
            ],
            ticketsCreated: [1, 2, 2, 3, 2, 1, 2, 3, 2, 2, 3, 2, 1, 2, 3, 2, 2, 3, 2, 1, 2, 3, 2, 2, 3, 4, 5, 3, 4, 3],
            byStatus: [
                { label: "Open", value: 7, color: "#0087e2" },
                { label: "Closed", value: 11, color: "#12a364" },
                { label: "Pending", value: 1, color: "#f59e0b" },
                { label: "Active", value: 3, color: "#7c3aed" },
            ],
            byPriority: [
                { label: "Low", value: 9, color: "#94a3b8" },
                { label: "Medium", value: 9, color: "#c4a882" },
                { label: "High", value: 4, color: "#eab308" },
            ],
            byDepartment: [
                { label: "Customer Support", value: 14, color: "#92400e" },
                { label: "Sales", value: 5, color: "#7c3aed" },
                { label: "Onboarding", value: 3, color: "#d97706" },
            ],
        },
    },
    "Marcus Webb": {
        stats: {
            open: 4, active: 1, inProgress: 0, overdue: 3, pending: 2,
            total: 15, clarification: 0, confirmation: 0, closed: 5,
        },
        dashboard: {
            topAgents: [
                { name: "Marcus Webb", resolved: 8 },
                { name: "John Doe", resolved: 4 },
            ],
            ticketsCreated: [0, 1, 0, 1, 2, 1, 0, 1, 2, 1, 0, 1, 2, 1, 1, 2, 1, 0, 1, 2, 1, 2, 1, 0, 1, 2, 3, 2, 2, 1],
            byStatus: [
                { label: "Open", value: 4, color: "#0087e2" },
                { label: "Closed", value: 5, color: "#12a364" },
                { label: "Pending", value: 2, color: "#f59e0b" },
                { label: "Active", value: 1, color: "#7c3aed" },
            ],
            byPriority: [
                { label: "Low", value: 3, color: "#94a3b8" },
                { label: "Medium", value: 8, color: "#c4a882" },
                { label: "High", value: 4, color: "#eab308" },
            ],
            byDepartment: [
                { label: "Billing", value: 11, color: "#059669" },
                { label: "Customer Support", value: 3, color: "#92400e" },
                { label: "Sales", value: 1, color: "#7c3aed" },
            ],
        },
    },
    "Laura Bennett": {
        stats: {
            open: 2, active: 0, inProgress: 0, overdue: 0, pending: 1,
            total: 4, clarification: 1, confirmation: 0, closed: 1,
        },
        dashboard: {
            topAgents: [
                { name: "Laura Bennett", resolved: 1 },
                { name: "Riya Patel", resolved: 1 },
            ],
            ticketsCreated: [0, 0, 1, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 1, 0, 0, 1],
            byStatus: [
                { label: "Open", value: 2, color: "#0087e2" },
                { label: "Closed", value: 1, color: "#12a364" },
                { label: "Pending", value: 1, color: "#f59e0b" },
                { label: "Active", value: 0, color: "#7c3aed" },
            ],
            byPriority: [
                { label: "Low", value: 2, color: "#94a3b8" },
                { label: "Medium", value: 1, color: "#c4a882" },
                { label: "High", value: 1, color: "#eab308" },
            ],
            byDepartment: [
                { label: "Customer Support", value: 3, color: "#92400e" },
                { label: "Billing", value: 1, color: "#059669" },
            ],
        },
    },
};

export function getMockDashboardForUser(userName) {
    return MOCK_DASHBOARD_BY_USER[userName] ?? MOCK_DASHBOARD_BY_USER[MOCK_USER.name];
}

export const TICKET_STATUS_OPTIONS = [
    { value: "", label: "All statuses" },
    { value: "open", label: "Open", color: "#0087e2" },
    { value: "active", label: "Active", color: "#12a364" },
    { value: "pending", label: "Pending", color: "#f59e0b" },
    { value: "spam", label: "Spam", color: "#dc2626" },
    { value: "needs_more", label: "Needs More Details", color: "#eab308" },
    { value: "rejected", label: "Rejected", color: "#991b1b" },
    { value: "closed", label: "Closed", color: "#6b7280" },
];

export const TICKET_PRIORITY_OPTIONS = [
    { value: "low", label: "Low", color: "#94a3b8" },
    { value: "medium", label: "Medium", color: "#d97706" },
    { value: "high", label: "High", color: "#dc2626" },
    { value: "urgent", label: "Urgent", color: "#7f1d1d" },
];

export const TICKET_SUBMENU = [
    { id: "all", label: "All Tickets" },
    { id: "open", label: "Open" },
    { id: "active", label: "Active" },
    { id: "unassigned", label: "Unassigned" },
    { id: "mine", label: "My Tickets" },
    { id: "critical", label: "Critical" },
    { id: "high", label: "High" },
    { id: "need_confirmation", label: "Need Confirmation" },
    { id: "need_clarification", label: "Need Clarification" },
    { id: "needs_more", label: "Needs More Details" },
    { id: "rejected", label: "Rejected" },
    { id: "pending", label: "Pending" },
    { id: "spam", label: "Spam" },
];

export const MOCK_TYPES = [
    { value: "", label: "Select Type", color: "#9ca3af" },
    { value: "question", label: "Question", color: "#0087e2" },
    { value: "incident", label: "Incident", color: "#dc2626" },
    { value: "feature", label: "Feature Request", color: "#7c3aed" },
];

export const MOCK_TAGS = [
    { value: "", label: "Select Tag", color: "#9ca3af" },
    { value: "compliance", label: "Compliance", color: "#059669" },
    { value: "billing", label: "Billing", color: "#d97706" },
    { value: "urgent", label: "Urgent", color: "#dc2626" },
];

export const MOCK_TICKETS = [
    {
        id: 1101,
        ticketId: "demo1101",
        ticketNumber: "TKT-000000001",
        title: "Export ticket history for compliance",
        preview: "We need to export all tickets from the last 12 months for an audit…",
        description: "We need to export all tickets from the last 12 months for an audit. Is there a bulk export feature available?",
        status: "open",
        statusLabel: "Open",
        department: "Customer Support",
        deptColor: "#92400e",
        priority: "Medium",
        priorityKey: "medium",
        assignee: null,
        replied: "-",
        created: "23h ago",
        displayDate: "6/6/2023, 10:23 AM",
        createdBy: "Laura Bennett",
        customerName: "Laura Bennett",
        customerInitials: "LB",
        customerEmail: "lbennett@gmail.com",
        due: "-",
        replies: [],
        notes: [],
        messages: [{ from: "customer", text: "We need to export all tickets from the last 12 months for an audit. Is there a bulk export feature available?" }],
    },
    {
        id: 1100,
        ticketId: "demo1100",
        title: "Email reply not appearing in thread",
        preview: "Customer sent a follow-up but it is missing from the ticket…",
        status: "active",
        statusLabel: "Active",
        department: "Development",
        deptColor: "#0087e2",
        priority: "High",
        priorityKey: "high",
        assignee: "JS",
        replied: "2h ago",
        created: "3h ago",
        createdBy: "Jane Smith",
        due: "next 2d",
        messages: [{ from: "customer", text: "Customer sent a follow-up but it is missing from the ticket." }],
    },
    {
        id: 1099,
        ticketId: "demo1099",
        title: "Production login issues this morning",
        preview: "Several users cannot sign in after the overnight deploy…",
        status: "pending",
        statusLabel: "Pending",
        department: "Customer Support",
        deptColor: "#92400e",
        priority: "Urgent",
        priorityKey: "urgent",
        assignee: "JS",
        replied: "1h ago",
        created: "3h ago",
        createdBy: "Jane Smith",
        due: "today",
        messages: [{ from: "customer", text: "Several users cannot sign in after the overnight deploy." }],
    },
    {
        id: 1098,
        ticketId: "demo1098",
        title: "Wrong invoice amount on March bill",
        preview: "The total on our March invoice does not match the plan…",
        status: "spam",
        statusLabel: "Spam",
        department: "Billing",
        deptColor: "#059669",
        priority: "Medium",
        priorityKey: "medium",
        assignee: null,
        replied: "-",
        created: "6h ago",
        createdBy: "Emma Wright",
        due: "-",
        messages: [{ from: "customer", text: "The total on our March invoice does not match the plan." }],
    },
    {
        id: 1097,
        ticketId: "demo1097",
        title: "Need clarification on API rate limits",
        preview: "Can you confirm the hourly cap for our workspace?",
        status: "needs_more",
        statusLabel: "Needs More Details",
        department: "Development",
        deptColor: "#0087e2",
        priority: "High",
        priorityKey: "high",
        assignee: "AM",
        replied: "4h ago",
        created: "8h ago",
        createdBy: "Alex Morgan",
        due: "next 3d",
        messages: [{ from: "customer", text: "Can you confirm the hourly cap for our workspace?" }],
    },
    {
        id: 1096,
        ticketId: "demo1096",
        title: "Rejected duplicate onboarding request",
        preview: "Please ignore the previous onboarding form submission…",
        status: "rejected",
        statusLabel: "Rejected",
        department: "Onboarding",
        deptColor: "#d97706",
        priority: "Low",
        priorityKey: "low",
        assignee: null,
        replied: "-",
        created: "10h ago",
        createdBy: "Jordan Kim",
        due: "-",
        messages: [{ from: "customer", text: "Please ignore the previous onboarding form submission." }],
    },
    {
        id: 1095,
        ticketId: "demo1095",
        title: "Widget styling on mobile Safari",
        preview: "The embed launcher overlaps our footer on iPhone…",
        status: "open",
        statusLabel: "Open",
        department: "Social Media",
        deptColor: "#db2777",
        priority: "Medium",
        priorityKey: "medium",
        assignee: "RP",
        replied: "-",
        created: "12h ago",
        createdBy: "Riya Patel",
        due: "next 5d",
        messages: [{ from: "customer", text: "The embed launcher overlaps our footer on iPhone." }],
    },
    {
        id: 1094,
        ticketId: "demo1094",
        title: "Weekly summary email not received",
        preview: "Our team did not get the scheduled digest yesterday…",
        status: "open",
        statusLabel: "Open",
        department: "Customer Support",
        deptColor: "#92400e",
        priority: "High",
        priorityKey: "high",
        assignee: "MW",
        replied: "5h ago",
        created: "14h ago",
        createdBy: "Marcus Webb",
        due: "next 1d",
        messages: [{ from: "customer", text: "Our team did not get the scheduled digest yesterday." }],
    },
    {
        id: 1093,
        ticketId: "demo1093",
        title: "Assignee dropdown empty for agents",
        preview: "Agents cannot see teammates in the assignee list…",
        status: "active",
        statusLabel: "Active",
        department: "Development",
        deptColor: "#0087e2",
        priority: "High",
        priorityKey: "high",
        assignee: "CL",
        replied: "30m ago",
        created: "16h ago",
        createdBy: "Ron Kline",
        due: "next 2d",
        messages: [{ from: "customer", text: "Agents cannot see teammates in the assignee list." }],
    },
    {
        id: 1092,
        ticketId: "demo1092",
        title: "Refund for order #5521",
        preview: "Customer was charged twice for the same subscription…",
        status: "pending",
        statusLabel: "Pending",
        department: "Billing",
        deptColor: "#059669",
        priority: "Urgent",
        priorityKey: "urgent",
        assignee: "CL",
        replied: "-",
        created: "18h ago",
        createdBy: "Sarah Connor",
        due: "today",
        messages: [{ from: "customer", text: "Customer was charged twice for the same subscription." }],
    },
];

export const MOCK_NAV = [
    { id: "dashboard", label: "Dashboard", icon: "dashboard" },
    {
        id: "tickets",
        label: "Tickets",
        icon: "tickets",
        children: TICKET_SUBMENU,
    },
    { id: "users", label: "Users", icon: "users" },
    { id: "departments", label: "Departments", icon: "departments" },
    { id: "emails", label: "Incoming Emails", icon: "emails" },
    { id: "reports", label: "Reports", icon: "reports" },
];

export const REPORT_CHARTS = {
    volume: {
        title: "Ticket volume",
        subtitle: "Last 30 days",
        labels: ["May 15", "May 22", "May 29", "Jun 5", "Jun 12"],
        values: [12, 18, 14, 22, 19],
    },
    response: {
        title: "First response time",
        subtitle: "Hours · last 7 days",
        labels: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
        values: [2.1, 1.8, 3.2, 1.4, 2.6, 0.9, 1.2],
    },
    priority: {
        title: "By priority",
        segments: [
            { label: "Low", value: 28, color: "#94a3b8" },
            { label: "Medium", value: 45, color: "#d97706" },
            { label: "High", value: 27, color: "#dc2626" },
        ],
    },
    team: {
        title: "Team performance",
        rows: [
            { name: "John Doe", replies: 109, solved: 42 },
            { name: "Jane Smith", replies: 76, solved: 31 },
            { name: "Alex Morgan", replies: 54, solved: 28 },
        ],
    },
    status: {
        title: "By status",
        subtitle: "Current queue",
        bars: [
            { label: "Open", value: 23, color: "#0087e2" },
            { label: "Closed", value: 10, color: "#12a364" },
            { label: "Pending", value: 3, color: "#f59e0b" },
            { label: "Active", value: 6, color: "#7c3aed" },
        ],
    },
    departments: {
        title: "By department",
        subtitle: "All tickets",
        rows: [
            { label: "Development", value: 178, color: "#0087e2" },
            { label: "Customer Support", value: 53, color: "#92400e" },
            { label: "Billing", value: 34, color: "#059669" },
        ],
    },
    resolution: {
        title: "Resolution time",
        subtitle: "Last 30 days",
        segments: [
            { label: "Same day", value: 42, color: "#12a364" },
            { label: "Within 3 days", value: 38, color: "#0087e2" },
            { label: "Over 3 days", value: 20, color: "#d97706" },
        ],
    },
};

export const WORKFLOW_STATUSES = [
    { id: "open", label: "Open" },
    { id: "active", label: "Active" },
    { id: "pending", label: "Pending" },
    { id: "closed", label: "Closed" },
];

export const WORKFLOW_DEMO_TICKET = {
    id: 1043,
    source: "Email",
    title: "Refund for order #1842",
    customer: "Sarah Connor",
    total: "$37.48",
    items: ["2× Margherita Pizza, $24.00", "1× Garlic bread, $6.00"],
    readyIn: "12:00",
};

function normalizeFilterValues(value) {
    if (Array.isArray(value)) return value.filter(Boolean);
    return value ? [value] : [];
}

/** Filter tickets by sidebar submenu + toolbar filters + search */
export function filterMockTickets(
    tickets,
    {
        submenu = "all",
        status = [],
        department = [],
        priority = [],
        assignee = [],
        search = "",
    },
    currentUserInitials = "CL"
) {
    const statusFilters = normalizeFilterValues(status);
    const departmentFilters = normalizeFilterValues(department);
    const priorityFilters = normalizeFilterValues(priority);
    const assigneeFilters = normalizeFilterValues(assignee);
    let list = [...tickets];

    switch (submenu) {
        case "open":
            list = list.filter((t) => t.status === "open");
            break;
        case "active":
            list = list.filter((t) => t.status === "active");
            break;
        case "unassigned":
            list = list.filter((t) => !t.assignee);
            break;
        case "mine":
            list = list.filter((t) => t.assignee === currentUserInitials);
            break;
        case "critical":
            list = list.filter((t) => t.critical || t.priorityKey === "high" || t.priorityKey === "urgent");
            break;
        case "high":
            list = list.filter((t) => t.priorityKey === "high" || t.priorityKey === "urgent");
            break;
        case "pending":
            list = list.filter((t) => t.status === "pending");
            break;
        case "spam":
            list = list.filter((t) => t.status === "spam");
            break;
        case "needs_more":
            list = list.filter((t) => t.status === "needs_more");
            break;
        case "rejected":
            list = list.filter((t) => t.status === "rejected");
            break;
        default:
            break;
    }

    if (statusFilters.length) {
        list = list.filter((t) => statusFilters.includes(t.status));
    }
    if (departmentFilters.length) {
        list = list.filter((t) => departmentFilters.includes(t.department));
    }
    if (priorityFilters.length) {
        list = list.filter((t) => priorityFilters.includes(t.priorityKey));
    }
    if (assigneeFilters.length) {
        list = list.filter((t) => assigneeFilters.includes(t.assignee || ""));
    }

    const query = search.trim().toLowerCase();
    if (query) {
        list = list.filter((t) => (
            t.title.toLowerCase().includes(query)
            || t.preview.toLowerCase().includes(query)
            || t.createdBy.toLowerCase().includes(query)
            || t.department.toLowerCase().includes(query)
            || t.ticketId.toLowerCase().includes(query)
            || (t.assignee && t.assignee.toLowerCase().includes(query))
        ));
    }

    return list;
}
